import {
  useReactTable,
  getCoreRowModel,
  flexRender,
  type ColumnDef,
} from '@tanstack/react-table';
import { Table, TextInput, Pagination, ActionIcon, Image, Text, Flex, Box } from '@mantine/core';
import { IconSearch, IconTrash, IconChevronUp, IconChevronDown } from '@tabler/icons-react';
import type { GridColumn } from '../lib/types';

interface DataGridProps {
  data: Record<string, unknown>[];
  columns: GridColumn[];
  total: number;
  page: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  sortBy: string | undefined;
  sortDir: 'asc' | 'desc';
  onSortChange: (column: string, dir: 'asc' | 'desc') => void;
  search: string;
  onSearchChange: (search: string) => void;
  onRowClick?: (row: Record<string, unknown>) => void;
  onDelete?: (row: Record<string, unknown>) => void;
}

function formatCellValue(value: unknown, col: GridColumn, row: Record<string, unknown>): React.ReactNode {
  if (col.render) return col.render(value, row);
  if (value === null || value === undefined) return '—';
  if (col.type === 'boolean') return value ? 'Yes' : 'No';
  if (col.type === 'date' && (value instanceof Date || typeof value === 'string')) {
    return new Date(value as string).toLocaleDateString();
  }
  if (col.type === 'image' && typeof value === 'string') {
    return <Image src={`/uploads/${value}`} alt="" h={40} w="auto" fit="cover" />;
  }
  return String(value);
}

export default function DataGrid({
  data, columns, total, page, pageSize, onPageChange,
  sortBy, sortDir, onSortChange, search, onSearchChange, onRowClick, onDelete,
}: DataGridProps) {
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  const tableColumns: ColumnDef<Record<string, unknown>>[] = columns.map((col) => ({
    id: col.key,
    header: col.title,
    accessorKey: col.key,
    size: col.width ? parseInt(col.width) : undefined,
    cell: ({ row, column }) => {
      const colDef = columns.find((c) => c.key === column.id);
      if (!colDef) return null;
      return formatCellValue(row.original[colDef.key], colDef, row.original);
    },
  }));

  if (onDelete) {
    tableColumns.push({
      id: '__actions',
      header: '',
      cell: ({ row }) => (
        <ActionIcon
          variant="subtle"
          color="red"
          onClick={(e) => {
            e.stopPropagation();
            onDelete(row.original);
          }}
          aria-label="Delete"
        >
          <IconTrash size={16} />
        </ActionIcon>
      ),
    });
  }

  const table = useReactTable({
    data,
    columns: tableColumns,
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
    manualSorting: true,
    pageCount: totalPages,
  });

  function handleHeaderClick(colKey: string, sortable?: boolean) {
    if (!sortable) return;
    const newDir = sortBy === colKey && sortDir === 'asc' ? 'desc' : 'asc';
    onSortChange(colKey, newDir);
  }

  return (
    <Box>
      <TextInput
        type="search"
        placeholder="Search…"
        value={search}
        onChange={(e) => onSearchChange(e.currentTarget.value)}
        leftSection={<IconSearch size={16} />}
        w={280}
        mb="sm"
      />

      <Table highlightOnHover withTableBorder>
        <Table.Thead>
          {table.getHeaderGroups().map((headerGroup) => (
            <Table.Tr key={headerGroup.id}>
              {headerGroup.headers.map((header) => {
                const col = columns.find((c) => c.key === header.id);
                const isSortable = col?.sortable ?? false;
                const isActive = sortBy === header.id;
                return (
                  <Table.Th
                    key={header.id}
                    onClick={() => handleHeaderClick(header.id, isSortable)}
                    style={{
                      cursor: isSortable ? 'pointer' : 'default',
                      userSelect: 'none',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    <Flex align="center" gap="xs" component="span">
                      {flexRender(header.column.columnDef.header, header.getContext())}
                      {isSortable && isActive && (
                        sortDir === 'asc' ? <IconChevronUp size={14} /> : <IconChevronDown size={14} />
                      )}
                    </Flex>
                  </Table.Th>
                );
              })}
            </Table.Tr>
          ))}
        </Table.Thead>
        <Table.Tbody>
          {table.getRowModel().rows.map((row) => (
            <Table.Tr
              key={row.id}
              onClick={() => onRowClick?.(row.original)}
              style={{ cursor: onRowClick ? 'pointer' : 'default' }}
            >
              {row.getVisibleCells().map((cell) => (
                <Table.Td key={cell.id}>
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </Table.Td>
              ))}
            </Table.Tr>
          ))}
          {data.length === 0 && (
            <Table.Tr>
              <Table.Td colSpan={tableColumns.length}>
                <Text ta="center" c="dimmed" py="md">No records found.</Text>
              </Table.Td>
            </Table.Tr>
          )}
        </Table.Tbody>
      </Table>

      <Flex align="center" justify="space-between" mt="md">
        <Text size="sm" c="dimmed">{total} total</Text>
        <Pagination value={page} total={totalPages} onChange={onPageChange} />
      </Flex>
    </Box>
  );
}
